// src/pages/Connector/PrayerTiming/EditPrayerTimeDialog.js
import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Divider,
    Box
} from '@mui/material';

export default function EditPrayerTimeDialog({ open, onClose, onSave, prayerTime }) {
    const [formData, setFormData] = useState({
        fajr: '',
        sunrise: '',
        dhuhr: '',
        asr: '',
        asr_hanafi: '',
        maghrib: '',
        isha: '',
        imsak: '',
        midnight: '',
        fajr_adjustment: 0,
        dhuhr_adjustment: 0,
        asr_adjustment: 0,
        maghrib_adjustment: 0,
        isha_adjustment: 0,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Initialize form when prayerTime changes
    useEffect(() => {
        if (prayerTime) {
            setFormData({
                fajr: prayerTime.fajr || '',
                sunrise: prayerTime.sunrise || '',
                dhuhr: prayerTime.dhuhr || '',
                asr: prayerTime.asr || '',
                asr_hanafi: prayerTime.asr_hanafi || '',
                maghrib: prayerTime.maghrib || '',
                isha: prayerTime.isha || '',
                imsak: prayerTime.imsak || '',
                midnight: prayerTime.midnight || '',
                fajr_adjustment: prayerTime.fajr_adjustment || 0,
                dhuhr_adjustment: prayerTime.dhuhr_adjustment || 0,
                asr_adjustment: prayerTime.asr_adjustment || 0,
                maghrib_adjustment: prayerTime.maghrib_adjustment || 0,
                isha_adjustment: prayerTime.isha_adjustment || 0,
            });
        }
        setError('');
    }, [prayerTime]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            await onSave(formData);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update prayer time');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    Edit Prayer Time
                    <Typography variant="subtitle2" color="text.secondary">
                        {formatDate(prayerTime?.date)}
                    </Typography>
                    {prayerTime?.hijri_date && (
                        <Typography variant="caption" color="text.secondary">
                            Hijri: {prayerTime.hijri_date}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Prayer Times
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Fajr"
                                name="fajr"
                                value={formData.fajr}
                                onChange={handleChange}
                                placeholder="e.g., 05:30"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Sunrise"
                                name="sunrise"
                                value={formData.sunrise}
                                onChange={handleChange}
                                placeholder="e.g., 06:45"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Dhuhr"
                                name="dhuhr"
                                value={formData.dhuhr}
                                onChange={handleChange}
                                placeholder="e.g., 12:30"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Asr (Shafi'i)"
                                name="asr"
                                value={formData.asr}
                                onChange={handleChange}
                                placeholder="e.g., 15:45"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Asr (Hanafi)"
                                name="asr_hanafi"
                                value={formData.asr_hanafi}
                                onChange={handleChange}
                                placeholder="e.g., 16:30"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Maghrib"
                                name="maghrib"
                                value={formData.maghrib}
                                onChange={handleChange}
                                placeholder="e.g., 18:15"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Isha"
                                name="isha"
                                value={formData.isha}
                                onChange={handleChange}
                                placeholder="e.g., 19:30"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Imsak"
                                name="imsak"
                                value={formData.imsak}
                                onChange={handleChange}
                                placeholder="e.g., 05:20"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Midnight"
                                name="midnight"
                                value={formData.midnight}
                                onChange={handleChange}
                                placeholder="e.g., 00:00"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Adjustments (in minutes)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Adjust prayer times from the calculated values (positive or negative minutes)
                            </Typography>
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Fajr Adj."
                                name="fajr_adjustment"
                                type="number"
                                value={formData.fajr_adjustment}
                                onChange={handleChange}
                                size="small"
                                inputProps={{ min: -60, max: 60 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Dhuhr Adj."
                                name="dhuhr_adjustment"
                                type="number"
                                value={formData.dhuhr_adjustment}
                                onChange={handleChange}
                                size="small"
                                inputProps={{ min: -60, max: 60 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Asr Adj."
                                name="asr_adjustment"
                                type="number"
                                value={formData.asr_adjustment}
                                onChange={handleChange}
                                size="small"
                                inputProps={{ min: -60, max: 60 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Maghrib Adj."
                                name="maghrib_adjustment"
                                type="number"
                                value={formData.maghrib_adjustment}
                                onChange={handleChange}
                                size="small"
                                inputProps={{ min: -60, max: 60 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Isha Adj."
                                name="isha_adjustment"
                                type="number"
                                value={formData.isha_adjustment}
                                onChange={handleChange}
                                size="small"
                                inputProps={{ min: -60, max: 60 }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                        <Alert severity="info" icon={false}>
                            <Typography variant="caption">
                                Note: After editing prayer times, you'll need to re-train the data to update the chatbot's knowledge.
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
